import React, { useState, useEffect } from 'react';
import {Box, Button, AppBar, Toolbar, Grid, Typography, Modal, LinearProgress, CircularProgress} from '@mui/material';
import { Add } from '@mui/icons-material';
import HeaderAvatar from "../components/HeaderAvatar";
import PresentationEditor from "../components/PresentationEditor";
import { usePermissions } from "../components/PermissionsContext";
import { fetchProjects, uploadPresentation, getSlides, getChapters, addChapter, getChapterSlides, addSlideToChapter, fetchProject } from '../services/api';
import { useNavigate, useParams } from 'react-router-dom';

interface Slide {
    id: string;
    name: string;
    preview: string;
    // Add other properties as needed
}

interface Presentation {
    id: string;
    name: string;
    photo_url: string;
    clm_type_id: string;
    first_slide: Slide
    // Add other properties as needed
}
interface PresentationsListProps {
    presentations: Presentation[];
    onPresentationClick: (presentation: Presentation) => void;
}


interface Chapter {
    id: string;
    name: string;
    project_id: string;
    sort: number;
    external_id?: string;
}
interface ChapterBoxProps {
    chapter: Chapter;
    onAddSlide: (chapterId: string) => void;
}
interface UploadModalProps {
    open: boolean;
    onClose: () => void;
    progress: number;
}
interface PresentationEditorProps {
    presentation: Presentation;
    slides: Slide[];
    chapters: Chapter[];
    onUpload: (file: File) => Promise<void>;
    onAddChapter: () => void;
    onAddSlideToChapter: (chapterId: string) => void;
}


const Presentations: React.FC = () => {
    const { permissions } = usePermissions();
    const [presentations, setPresentations] = useState([]);
    const [slides, setSlides] = useState([]);
    const [chapters, setChapters] = useState<Chapter[]>([]);
    const [currentPresentation, setCurrentPresentation] = useState<Presentation | null>(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [progressText, setProgressText] = useState<string>('Пожалуйста, дождитесь завершения загрузки');

    useEffect(() => {
        loadPresentations();
    }, []);


    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        if (id) {
            fetchProject(id).then(setCurrentPresentation);
        }else{
            setCurrentPresentation(null)
        }
    }, [id]);

    const handlePresentationClick = (presentation: Presentation) => {
        setCurrentPresentation(presentation);
        navigate(`/presentations/${presentation.id}`);
    };

    const loadPresentations = async () => {
        const data = await fetchProjects(1, 100);
        setPresentations(data.items);
    };

    const PresentationsList: React.FC<PresentationsListProps> = ({ presentations, onPresentationClick }) => (
        <Box className='presentationItems'>
            {presentations.map((presentation) => (
                <Box className='presentationItem' key={presentation.id} onClick={() => onPresentationClick(presentation)}>
                    {presentation.first_slide!==null && presentation.first_slide.preview ?
                        <Box>
                            <img src={presentation.first_slide.preview} alt={presentation.first_slide.name}/>
                        </Box>
                        : null}
                    <Box className='presentationItemText'>
                        {presentation.name}
                    </Box>
                </Box>
            ))}
        </Box>
    );



    const ChapterBox: React.FC<ChapterBoxProps> = ({ chapter, onAddSlide }) => (
        <Box mt={2}>
            <Typography variant="h6">{chapter.name}</Typography>
            <Button onClick={() => onAddSlide(chapter.id)}>Добавить слайд</Button>
            {/* Здесь будет список слайдов главы */}
        </Box>
    );


    const handleUploadPresentation = async (file: File) => {
        if (!currentPresentation) {
            console.error('No presentation selected');
            return;
        }

        setIsUploadModalOpen(true);
        try {
            const response = await uploadPresentation(currentPresentation.id, file, (progress) => {
                setUploadProgress(progress);
                if (progress == 100){
                    setProgressText("Идет распаковка и оптимизация")
                }
            });
            // After successful upload, refresh the slides

            const updatedSlides = await getSlides(currentPresentation.id);
            setSlides(updatedSlides);
        } catch (error) {
            console.error('Error uploading presentation:', error);
            // Handle error (e.g., show an error message to the user)
        } finally {
            setProgressText("Пожалуйста, дождитесь завершения загрузки")
            setIsUploadModalOpen(false);
            setUploadProgress(0);
        }
    };

    return (
        <Box>
            <AppBar position="static">
                <Toolbar className='toolbar'>
                    <Box className='toolbarTitle'>{currentPresentation ?
                        <Box>{currentPresentation.name}</Box>
                        :
                        <Box>Презентации <span>{presentations.length}</span></Box>
                    }</Box>
                    <Button className="addToobarBtn" variant="contained" onClick={() => {}}><Add /></Button>
                    <HeaderAvatar/>
                </Toolbar>
            </AppBar>

            {/* Основной контент */}
            {!currentPresentation ? (
                <PresentationsList presentations={presentations} onPresentationClick={handlePresentationClick} />
            ) : (
                <PresentationEditor
                    presentation={currentPresentation}
                    onUpload={handleUploadPresentation}
                />
            )}


            {/* Модальное окно загрузки */}
            <Modal open={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)}>
                <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                    <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 600 }}>
                        <Box sx={{ /* стили */ }}>
                            <Typography sx={{mb: 3}}>{progressText}</Typography>
                            {uploadProgress === 100 ?
                                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                                    <CircularProgress style={{color: '#3a485b'}}/>
                                </Box>
                                :
                                <Box>
                                    <LinearProgress variant="determinate" value={uploadProgress}/>
                                    <Typography>{uploadProgress}%</Typography>
                                </Box>
                            }
                        </Box>
                    </Box>
                </Box>
            </Modal>

        </Box>
    );
};

export default Presentations;
